import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useLang } from '../context/LangContext';
import { formatPriceByLanguage } from '../utils/currency';
import { formatLiveTime } from '../utils/dateUtils';
import AuctionHouseComponent from './common/AuctionHouse';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
`;

const Content = styled.div`
  padding: 0px 0px 40px 0px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: auto;
  gap: 16px;
`;

// 경매 정보 텍스트 영역 (column 구조, 좌측 정렬)
const AuctionInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const AuctionTitle = styled.div`
  font-size: 20px;
  color: #000;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const LotId = styled.h3`
  font-size: 16px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
`;

// 가격 정보 섹션
const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// 경매 정보와 가격 정보를 묶는 컨테이너
const AuctionPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// 라이브 정보 컨테이너
const LiveInfoContainer = styled.div`
  background-color: #f3f6f6;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

// 라이브 배지 (작은 빨간 원)
const LiveBadge = styled.div`
  background-color: #ed2424;
  border-radius: 50%;
  width: 6px;
  height: 6px;
  flex-shrink: 0;
`;

// 라이브 텍스트
const LiveText = styled.span`
  font-size: 12px;
  color: #262626;
  font-weight: 600;
`;

// 라이브 배지 컨테이너 (원형 + 텍스트)
const LiveBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 라이브 시작 시간 텍스트
const LiveStartTime = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

// 라이브 타이머 컨테이너 (라이브 중일 때)
const LiveTimerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 타이머 박스 (검정색 배경)
const TimerBox = styled.div`
  background-color: #262626;
  color: white;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  width: 40px;
  height: 43px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 타이머 단위 텍스트
const TimerUnit = styled.span`
  font-size: 12px;
  color: #333;
  font-weight: 500;
`;

// 남은 시간 텍스트
const RemainingText = styled.span`
  font-size: 12px;
  color: #333;
  font-weight: 500;
`;

const PriceItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const PriceLabel = styled.span`
  font-size: 12px;
  color: #666666;
  font-weight: 500;
  text-align: left;
`;

const PriceValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;
  text-align: left;
`;

interface AuctionInfoProps {
  title?: string;
  auctionData?: {
    auctionHouse?: string;
    auctioneerIndex?: number; // auctioneerIndex 추가
    auction?: string;
    lotName?: string;
    index?: number; // 서버에서 받아온 index (화면에서는 LOT.{index}로 표시)
    code?: string; // 서버에서 제공하는 LOT 코드
    preBidStartTime?: string;
    preBidEndTime?: string;
    liveStartTime?: string;
    liveEndTime?: string;
    currentPrice?: number;
    startPrice?: number;
    liveStartPrice?: number;
    exchangeRate?: number; // 환율 추가
  };
  exchangeRate?: number; // props로도 받을 수 있도록
  showLiveStartPrice?: boolean; // LiveBid에서만 true로 설정
}

// 다국어 텍스트
const auctionInfoTexts = {
  ko: {
    title: '경매 정보',
    currentPrice: '현재가',
    startPrice: '시작가',
    liveStartPrice: '라이브 시작가',
    auctionStarted: '경매가 시작되었습니다',
    preBiddingInProgress: '사전입찰 진행중',
    hoursMinutesLeft: '시간',
    minutesLeft: '분 남음',
    liveStartText: '라이브 시작',
    remaining: '남음',
    liveUntilEnd: 'Live 종료까지',
    auctionEnded: '경매가 종료되었습니다'
  },
  en: {
    title: 'Auction Info',
    currentPrice: 'Current Price',
    startPrice: 'Starting Price',
    liveStartPrice: 'Live Starting Price',
    auctionStarted: 'Auction has started',
    preBiddingInProgress: 'Pre-bidding in progress',
    hoursMinutesLeft: 'hours',
    minutesLeft: 'minutes left',
    liveStartText: 'Live Start',
    remaining: 'left',
    liveUntilEnd: 'Until Live Ends',
    auctionEnded: 'Auction has ended'
  }
};

// 더미 데이터 제거 - 실제 API 데이터 사용

// KRW 가격 포맷팅 함수 - 언어별 단일 통화로 변경
const formatPriceByLang = (price: number, exchangeRate: number, lang: 'ko' | 'en'): string => {
  return formatPriceByLanguage(price, exchangeRate, lang);
};

// 라이브 상태 확인 함수
const getLiveStatus = (startTime: string, endTime: string): 'before' | 'starting-soon' | 'live' | 'ended' => {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(endTime);
  const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  if (now > end) return 'ended';
  if (now >= start) return 'live';
  if (nowDate.getTime() === startDate.getTime()) return 'starting-soon'; // 당일
  return 'before'; // 전날 이전
};

// 남은 시간 계산 (xxh xxm 형태)
const calculateRemainingTime = (targetTime: string): { hours: number; minutes: number } => {
  const now = new Date();
  const target = new Date(targetTime);
  const diff = target.getTime() - now.getTime();
  
  if (diff <= 0) return { hours: 0, minutes: 0 };
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { hours, minutes };
};

const AuctionInfo: React.FC<AuctionInfoProps> = ({ 
  title, 
  auctionData, 
  exchangeRate = 1300,
  showLiveStartPrice = false // 기본값은 false (PreBid에서 사용)
}) => {
  const [liveStatus, setLiveStatus] = useState<'before' | 'starting-soon' | 'live' | 'ended'>('before');
  const [remainingTime, setRemainingTime] = useState<{ hours: number; minutes: number }>({ hours: 0, minutes: 0 });
  const { lang } = useLang();
  const texts = auctionInfoTexts[lang];
  
  // useMemo로 currentAuctionData 최적화
  const currentAuctionData = useMemo(() => auctionData || {
    auctionHouse: '',
    auctioneerIndex: 0, // auctioneerIndex 기본값 추가
    auction: '',
    lotName: '',
    index: 0, // index 기본값
    code: '', // code 기본값 추가
    preBidStartTime: '',
    preBidEndTime: '',
    liveStartTime: '',
    liveEndTime: '',
    currentPrice: 0,
    startPrice: 0,
    liveStartPrice: 0,
    exchangeRate: 1300
  }, [auctionData]);
  
  // 환율은 auctionData에서 먼저, 그 다음 props에서, 마지막으로 기본값 사용
  const currentExchangeRate = currentAuctionData.exchangeRate || exchangeRate;

  useEffect(() => {
    const updateAuctionInfo = () => {
      // 라이브 상태 로직만 유지 (LiveInfoContainer용)
      if (currentAuctionData.liveStartTime && currentAuctionData.liveEndTime) {
        const liveStatus = getLiveStatus(currentAuctionData.liveStartTime, currentAuctionData.liveEndTime);
        setLiveStatus(liveStatus);
        
        if (liveStatus === 'starting-soon') {
          setRemainingTime(calculateRemainingTime(currentAuctionData.liveStartTime));
        } else if (liveStatus === 'live') {
          setRemainingTime(calculateRemainingTime(currentAuctionData.liveEndTime));
        }
      }
    };
    
    updateAuctionInfo();

    // 1분마다 시간 업데이트
    const interval = setInterval(updateAuctionInfo, 60000);

    return () => clearInterval(interval);
  }, [lang, currentAuctionData]);

  // LiveInfoContainer 내용 렌더링
  const renderLiveInfo = () => {
    const liveStartTime = currentAuctionData.liveStartTime;
    
    if (!liveStartTime) return null;
    
    switch (liveStatus) {
      case 'before':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge />
              <LiveText>LIVE</LiveText>
            </LiveBadgeContainer>
            <LiveStartTime>{formatLiveTime(liveStartTime, lang)}</LiveStartTime>
          </>
        );
      
      case 'starting-soon':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge />
              <LiveText>LIVE</LiveText>
            </LiveBadgeContainer>
            <LiveStartTime>{remainingTime.hours}h {remainingTime.minutes}m</LiveStartTime>
          </>
        );
      
      case 'live':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge />
              <LiveText>{texts.liveUntilEnd}</LiveText>
            </LiveBadgeContainer>
            <LiveTimerContainer>
              <TimerBox>{remainingTime.hours}</TimerBox>
              <TimerUnit>h</TimerUnit>
              <TimerBox>{remainingTime.minutes}</TimerBox>
              <TimerUnit>m</TimerUnit>
              <RemainingText>{texts.remaining}</RemainingText>
            </LiveTimerContainer>
          </>
        );
      
      case 'ended':
        return (
          <>
            <LiveBadgeContainer>
              <LiveBadge style={{ backgroundColor: '#999' }} />
              <LiveText>END</LiveText>
            </LiveBadgeContainer>
            <LiveStartTime>{texts.auctionEnded}</LiveStartTime>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container>
      {/* <FormTitle>{sectionTitle}</FormTitle> */}
      <Content>
        {/* 경매 정보와 가격 정보를 묶는 컨테이너 */}
        <AuctionPriceContainer>
          {/* 경매 정보 텍스트 */}
          <AuctionInfoSection>
            <AuctionHouseComponent 
              auctioneerName={currentAuctionData.auctionHouse || ''}
              auctioneerIndex={currentAuctionData.auctioneerIndex || 0}
            />
            <AuctionTitle>{currentAuctionData.lotName || ''}</AuctionTitle>
            <LotId>{currentAuctionData.code || `LOT.${currentAuctionData.index || ''}`}</LotId>
          </AuctionInfoSection>

          {/* 가격 정보 */}
          <PriceSection>
            {!showLiveStartPrice && (
              <PriceItem>
                <PriceLabel>{texts.currentPrice}</PriceLabel>
                <PriceValue>{formatPriceByLang(currentAuctionData.currentPrice || 0, currentExchangeRate, lang)}</PriceValue>
              </PriceItem>
            )}
            
            {showLiveStartPrice && (
              <PriceItem>
                <PriceLabel>{texts.liveStartPrice}</PriceLabel>
                <PriceValue>{formatPriceByLang(currentAuctionData.liveStartPrice || 0, currentExchangeRate, lang)}</PriceValue>
              </PriceItem>
            )}
            
            <PriceItem>
              <PriceLabel>{texts.startPrice}</PriceLabel>
              <PriceValue>{formatPriceByLang(currentAuctionData.startPrice || 0, currentExchangeRate, lang)}</PriceValue>
            </PriceItem>
          </PriceSection>
        </AuctionPriceContainer>

        {/* 라이브 정보 컨테이너 */}
        <LiveInfoContainer>
          {renderLiveInfo()}
        </LiveInfoContainer>

      </Content>
    </Container>
  );
};

export default AuctionInfo;
